import { AlertTriangle, ArchiveRestore, Database, Download, HardDrive, Plus, ShieldCheck, Trash2, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import type { DamagedProjectSummary } from '../db'
import { recoveryReasonLabel, type RecoverySnapshot } from '../recovery'
import { formatBytes, type LocalStorageStatus } from '../storage'
import type { DeckProject } from '../types'
import { formatDateTime } from '../utils'

type StorageAction =
  | { kind: 'restore' | 'delete-snapshot'; snapshot: RecoverySnapshot }
  | { kind: 'delete-damaged'; project: DamagedProjectSummary }
  | null

interface StorageRecoveryDialogProps {
  project: DeckProject | null
  snapshots: RecoverySnapshot[]
  snapshotsLoading: boolean
  status: LocalStorageStatus
  persistenceNotice: { tone: 'success' | 'warning'; text: string } | null
  damagedProjects: DamagedProjectSummary[]
  busy: boolean
  onClose: () => void
  onCreateSnapshot: () => void
  onRestore: (snapshot: RecoverySnapshot) => void
  onDelete: (snapshot: RecoverySnapshot) => void
  onExport: (snapshot: RecoverySnapshot) => void
  onDownloadDamaged: (project: DamagedProjectSummary) => void
  onDeleteDamaged: (project: DamagedProjectSummary) => void
  onRequestPersistence: () => void
}

export function StorageRecoveryDialog({
  project,
  snapshots,
  snapshotsLoading,
  status,
  persistenceNotice,
  damagedProjects,
  busy,
  onClose,
  onCreateSnapshot,
  onRestore,
  onDelete,
  onExport,
  onDownloadDamaged,
  onDeleteDamaged,
  onRequestPersistence,
}: StorageRecoveryDialogProps) {
  const [action, setAction] = useState<StorageAction>(null)
  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || busy) return
      action ? setAction(null) : onClose()
    }
    window.addEventListener('keydown', close)
    return () => window.removeEventListener('keydown', close)
  }, [action, busy, onClose])

  const ratio = status.ratio === undefined ? null : Math.min(1, status.ratio)
  const statusCopy = status.pressure === 'critical'
    ? 'Local storage is nearly full. Export important projects now.'
    : status.pressure === 'warning'
      ? 'Local storage is getting full. Large image imports may fail.'
      : 'Deckelier stores projects and recovery copies only in this browser.'
  const damagedDate = (value?: string) => value && Number.isFinite(Date.parse(value)) ? `Last dated ${formatDateTime(value)}` : 'Saved date unavailable'

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && !busy && onClose()}>
      <section className="modal storage-dialog" role="dialog" aria-modal="true" aria-labelledby="storage-title">
        <div className="storage-dialog-heading">
          <div><span className="modal-kicker">Local reliability</span><h2 id="storage-title">Storage & recovery</h2></div>
          <button className="icon-button" aria-label="Close storage and recovery" disabled={busy} onClick={onClose}><X size={17} /></button>
        </div>

        <div className={`storage-summary ${status.pressure}`}>
          <HardDrive size={22} />
          <div>
            <strong>{status.usage === undefined ? 'Storage estimate unavailable' : `${formatBytes(status.usage)} used of ${formatBytes(status.quota)}`}</strong>
            <span>{statusCopy}</span>
          </div>
          {ratio !== null && <div className="storage-meter" aria-label={`${Math.round(ratio * 100)} percent of local storage used`}><i style={{ width: `${ratio * 100}%` }} /></div>}
        </div>

        <div className="persistence-card">
          <ShieldCheck size={20} />
          <div>
            <strong>{status.persisted ? 'Browser storage protection is on' : status.persistenceSupported === false ? 'Storage protection is unavailable here' : 'Ask the browser to protect this data'}</strong>
            <span>{status.persisted ? 'The browser is less likely to automatically evict Deckelier data.' : status.persistenceSupported === false ? 'This browser does not expose the protection request. Local saving still works normally.' : 'The browser decides whether to grant this silently. It may not show a permission prompt, and this is not a backup.'}</span>
          </div>
          {!status.persisted && status.persistenceSupported !== false && <button className="button secondary compact" disabled={busy} onClick={onRequestPersistence}>Request protection</button>}
        </div>
        {persistenceNotice && <p className={`persistence-notice ${persistenceNotice.tone}`}><ShieldCheck size={16} /> {persistenceNotice.text}</p>}

        {damagedProjects.length > 0 && <>
          <div className="recovery-list-heading damaged-list-heading">
            <div><AlertTriangle size={16} /><strong>Unreadable projects</strong><span>{damagedProjects.length}</span></div>
          </div>
          <div className="recovery-list damaged-project-list">
            {damagedProjects.map((damaged) => (
              <article className="recovery-row damaged-project-row" key={damaged.id}>
                <div>
                  <strong>{damaged.name}</strong>
                  <span>{damagedDate(damaged.updatedAt)}</span>
                  <small>{damaged.reason}</small>
                </div>
                <div className="recovery-row-actions">
                  <button className="button secondary compact" disabled={busy} onClick={() => onDownloadDamaged(damaged)}><Download size={15} /> Raw JSON</button>
                  <button className="icon-button danger" aria-label={`Remove unreadable project ${damaged.name}`} disabled={busy} onClick={() => setAction({ kind: 'delete-damaged', project: damaged })}><Trash2 size={15} /></button>
                </div>
              </article>
            ))}
          </div>
          <p className="storage-damage-warning"><AlertTriangle size={16} /> Download the original record before removing it if you may want to inspect or repair it later. Healthy recovery copies remain available below.</p>
        </>}

        <div className="recovery-list-heading">
          <div><Database size={16} /><strong>Recovery copies</strong><span>{snapshots.length}</span></div>
          {project && <button className="button secondary compact" disabled={busy} onClick={onCreateSnapshot}><Plus size={15} /> Save current copy</button>}
        </div>

        <div className="recovery-list">
          {snapshotsLoading ? (
            <div className="recovery-empty"><ArchiveRestore className="spin" size={24} /><strong>Opening recovery history…</strong></div>
          ) : snapshots.length === 0 ? (
            <div className="recovery-empty"><ArchiveRestore size={24} /><strong>No recovery history yet</strong><span>Deckelier keeps periodic last-known-good copies as you work.</span></div>
          ) : snapshots.map((snapshot) => (
            <article className="recovery-row" key={snapshot.id}>
              <div>
                <strong>{snapshot.projectName}</strong>
                <span>{recoveryReasonLabel(snapshot.reason)} · {formatDateTime(snapshot.createdAt)}</span>
                <small>{snapshot.project.rows.length} cards · {formatBytes(snapshot.byteSize)}</small>
              </div>
              <div className="recovery-row-actions">
                <button className="button secondary compact" disabled={busy} onClick={() => setAction({ kind: 'restore', snapshot })}><ArchiveRestore size={15} /> Restore</button>
                <button className="icon-button" aria-label={`Download recovery copy for ${snapshot.projectName}`} disabled={busy} onClick={() => onExport(snapshot)}><Download size={15} /></button>
                <button className="icon-button danger" aria-label={`Delete recovery copy for ${snapshot.projectName}`} disabled={busy} onClick={() => setAction({ kind: 'delete-snapshot', snapshot })}><Trash2 size={15} /></button>
              </div>
            </article>
          ))}
        </div>
        <p className="storage-export-note">Recovery copies live in the same browser storage as your projects. Exported <strong>.deckelier</strong> files are still the durable backup.</p>
      </section>

      {action && (
        <div className="modal-backdrop nested-dialog-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && !busy && setAction(null)}>
          <section className="modal delete-dialog snapshot-action-dialog" role="alertdialog" aria-modal="true">
            <span className="modal-kicker">{action.kind === 'delete-damaged' ? 'Unreadable project' : 'Recovery history'}</span>
            <h2>{action.kind === 'restore' ? `Restore “${action.snapshot.projectName}”?` : action.kind === 'delete-damaged' ? `Remove “${action.project.name}”?` : 'Delete this recovery copy?'}</h2>
            <p className="delete-dialog-copy">
              {action.kind === 'restore'
                ? 'This makes the selected copy the current saved project. Deckelier keeps the present version in recovery history first.'
                : action.kind === 'delete-damaged'
                  ? 'This permanently removes the unreadable saved record. Any healthy recovery copies are kept, but the original damaged data cannot be recovered unless you download it first.'
                  : 'This removes only the selected historical copy. The current project is not changed.'}
            </p>
            <div className="modal-actions">
              <button className="button secondary" autoFocus disabled={busy} onClick={() => setAction(null)}>Cancel</button>
              <button className={action.kind === 'restore' ? 'button primary' : 'button danger'} disabled={busy} onClick={() => {
                if (action.kind === 'restore') onRestore(action.snapshot)
                else if (action.kind === 'delete-damaged') onDeleteDamaged(action.project)
                else onDelete(action.snapshot)
                setAction(null)
              }}>{busy ? 'Working…' : action.kind === 'restore' ? 'Restore copy' : action.kind === 'delete-damaged' ? 'Remove project' : 'Delete copy'}</button>
            </div>
          </section>
        </div>
      )}
    </div>
  )
}
